import { graphqlSchema } from './graphql.schema';
import { getCookies } from './document.cookie';

import { split, HttpLink, ApolloClient, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getMainDefinition } from '@apollo/client/utilities';

import { getQueryFromWindow } from './window.query';

import { BehaviorSubject } from 'rxjs';
import { ProfileQL, FiscalpopProfile } from '../models/Profile';

import * as LogRocket from 'logrocket';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient, Client } from 'graphql-ws';

const AUTH_TOKEN = 'mws_profileid';
const FP_ADMIN_AUTH_TOKEN = 'fp_internal';
// const REDIRECT_TOKEN = 'token';
const GESTOR_TOKEN = 'gestores_profileid';

const WS_URL = process.env.REACT_APP_WS_URL;
const HTTP_URL = process.env.REACT_APP_HTTP_URL;

function getSessionTokens() {
    const storage = localStorage.getItem(AUTH_TOKEN)
    const cookies = getCookies();
    const query = getQueryFromWindow();
    const token = (query[AUTH_TOKEN] ? btoa(query[AUTH_TOKEN]) : '') || storage || cookies[AUTH_TOKEN] || '';
    const isAdmin = query[FP_ADMIN_AUTH_TOKEN] || cookies[FP_ADMIN_AUTH_TOKEN]
    const isGestor = cookies[GESTOR_TOKEN];

    // COOKIE TYPE TOKENS are set by Back End and requires access to ML directly, trust to build "stashed" cookie
    /*
    console.log('[getSessionToken]: ', token, {
        query: query[AUTH_TOKEN],
        cookies: cookies[AUTH_TOKEN],
        storage: storage
    });
    */

    return { token, isAdmin, isGestor }
}

function setSessionFixer() {
    // This should be called before authLink and wsLink
    const query = getQueryFromWindow();
    const { token, isAdmin, isGestor } = getSessionTokens();
    if (token) {
        localStorage.setItem(AUTH_TOKEN, token);
    }
    if (query[AUTH_TOKEN]) {
        // Clean query from URL
        console.log('query: ', query);
        const cleanedQueries = Object.keys(query).filter(k => k !== AUTH_TOKEN).map(k => `${k}=${query[k]}`).join('&');
        const noQueryUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${!cleanedQueries ? '' : `?${cleanedQueries}`}`;
        window.history.replaceState({ path: noQueryUrl }, "", noQueryUrl);
    }
    return { token, isAdmin, isGestor }
}

class GraphQlClientService {
    private _userInvoked: boolean = false;
    private _isGestorUser: boolean = false;
    private _client: ApolloClient
    private _wsClient: Client;
    private _currentUserSubject: BehaviorSubject<ProfileQL> = new BehaviorSubject({ amazonProfile: null, fiscalpopProfile: null, fiscalpopProfileStatus: null, billing: null });
    constructor() {
        console.log('Setting Session Token');
        setSessionFixer()
        const authLink = setContext((_, { headers }) => {
            const { token, isAdmin, isGestor } = getSessionTokens()
            if ((isGestor || isAdmin) && !this._isGestorUser) {
                this._isGestorUser = true;
            }
            return {
                headers: {
                    ...headers,
                    authorization: token ? `${token}` : '',
                    fpadmin: isAdmin || ''
                }
            }
        })

        // Websockets
        const wsLink = new GraphQLWsLink(this.getWsClient());

        // Create an http link:
        const httpLink = new HttpLink({
            uri: HTTP_URL,
            fetch: window.fetch,
        });

        const authHttpLink = authLink.concat(httpLink);

        const splitLink = split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            wsLink,
            authHttpLink,
        );

        this._client = new ApolloClient({
            link: splitLink,
            cache: new InMemoryCache(),
        });


        // Tab Focus Session Checker for account change
        // ---------------------------------------------
        const forOnFocus = (() => {
            let { token: lastSeenToken } = getSessionTokens()
            const _forOnFocus = () => {
                let { token } = getSessionTokens()
                if (!lastSeenToken || !token) {
                    // don't do anything, just return
                    lastSeenToken = token;
                } else if (lastSeenToken !== token) {
                    lastSeenToken = token; // Avoids multiple triggers of alert notify
                    console.log('<WinFocus> session change detected:', lastSeenToken, token)
                    LogRocket.track('SessionMismatchError')
                    alert(`Se detectó una sessión de FiscalPOP diferente, se recargarán los datos antes de continuar.\n\nRecuerde que solo es posible tener una sesión de Amazon abierta a la vez.`);
                    // As lazyLoaded queries cannot be re-loaded, hard reset might be needed
                    document.location.reload()
                }
            }
            // >> NOTE: check onblur is not needed as there's no way to change token in page, only when another tab is opened with different profile_id query
            // >> >> As it's possible
            return _forOnFocus;
        })()
        window.onfocus = forOnFocus
    }

    private getWsClient() {
        if (!this._wsClient) {
            this._wsClient = createClient({
                url: WS_URL,
                connectionParams: () => ({
                    // Note: using a function here so it's evaluated fresh on each (re)connect
                    authorization: getSessionTokens().token
                })
            });
        }
        return this._wsClient;
    };

    private async disconnectWs () {
        if (this._wsClient) {
            await this._wsClient.dispose();
            this._wsClient = null;
        }
    };

    private compileCookieSessions(me: ProfileQL) {
        const cookies = getCookies();
        if (!cookies[AUTH_TOKEN]) {
            return;
        }
        if (this._isGestorUser) {
            console.warn('<compileCookieSessions> User is Gestor type')
            return;
        }
    }

    logSession(token: string) {
        if (token) {
            const domain = '.fiscalpop.com'
            localStorage.setItem(AUTH_TOKEN, token);
            document.cookie = `${AUTH_TOKEN}=${token};domain=${domain};`;
        }
        return this._invokeGQLuser();
    }

    logOut() {
        const storage = localStorage.getItem(AUTH_TOKEN)
        const cookies = getCookies();
        if (storage) {
            localStorage.removeItem(AUTH_TOKEN);
        }
        if (cookies[AUTH_TOKEN]) {
            console.log('Removing session')
            const domain = '.fiscalpop.com'
            document.cookie = `${AUTH_TOKEN}=;domain=${domain};expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        }
        this.disconnectWs();
        this.client.clearStore()
        this._currentUserSubject.next(null);
    }

    recallCurrentUser() {
        this._invokeGQLuser();
    }

    private _invokeGQLuser() {
        // To be called whenever user has been updated without using Subscriptions
        this._userInvoked = true;
        return this._client.query({
            query: graphqlSchema.PROFILE.me,
            fetchPolicy: 'network-only',
        })
            .then((response: { data: { me: ProfileQL } }) => {
                if (response.data.me) {
                    // Check if a refresh is needed  due to SP Refresh Fail and take user to Consent
                    if (response.data.me.amazonProfile?.spAccessLastGrantFail) {
                        console.log('Failed Token Refresh, move to consent')
                        const appId = `amzn1.sellerapps.app.b5727cc3-3fe4-4e1c-8232-2fe1588ec686`;
                        const appRedirect = `https://apimws.fiscalpop.com/spconsent`;
                        const state = response.data.me.amazonProfile._id;
                        if (!state) {
                            return this.logOut();
                        }
                        window.location.replace(`https://sellercentral.amazon.com.mx/apps/authorize/consent?application_id=${appId}&state=${state}&redirect_uri=${appRedirect}`)
                        return;
                    }
                    console.log('[_invokeGQLuser] Currrent User: ', response.data.me);
                    this._currentUserSubject.next(response.data.me);
                    this.compileCookieSessions(response.data.me);
                } else {
                    console.error('[_invokeGQLuser] Currrent User: ', response.data.me);
                    console.error(new Error('User needs Profile Id'))
                    this._currentUserSubject.next(response.data.me);
                }
                return response.data.me as ProfileQL;
            })
            .catch((error) => {
                console.error(`[_invokeGQLuser] eror! `, error);
                // >> Always try to recover login process (works for expired permissions)
                const cookies = getCookies();
                const query = getQueryFromWindow();
                const token = (query[AUTH_TOKEN] ? btoa(query[AUTH_TOKEN]) : '') || cookies[AUTH_TOKEN]
                if (token) {
                    const appId = `amzn1.sellerapps.app.b5727cc3-3fe4-4e1c-8232-2fe1588ec686`;
                    const appRedirect = `https://apimws.fiscalpop.com/spconsent`;
                    const preState = token;
                    if (!preState) {
                        return this.logOut();
                    }
                    const state = atob(preState);
                    window.location.replace(`https://sellercentral.amazon.com.mx/apps/authorize/consent?application_id=${appId}&state=${state}&redirect_uri=${appRedirect}`)
                    return;
                }
                this.logOut();
                // Remove cookies, this can be triggered by deleted accounts in DB directly
            })
    }

    modifyClientMLprofile(props: { [key: string]: any }) {
        const profile = this._currentUserSubject.value;
        const currentMLprofile = profile.amazonProfile;
        const newMLprofile = Object.assign({}, currentMLprofile, props);
        const nProfile: ProfileQL = {
            amazonProfile: newMLprofile,
            fiscalpopProfile: profile.fiscalpopProfile,
            fiscalpopProfileStatus: profile.fiscalpopProfileStatus,
            billing: profile.billing,
        }
        this._currentUserSubject.next(nProfile);
    }

    submitFiscalpopClient(client: FiscalpopProfile) {
        return this._client.mutate({
            mutation: graphqlSchema.FISCALPOP.SETUP.setFiscalpopClient,
            variables: {
                client
            }
        })
            .then((clientMutation) => {
                console.log('Client mutation: ', clientMutation);
                this._invokeGQLuser();
                return clientMutation;
            })
    }

    getCurrentUser() {
        console.log('Getting current user')
        if (!this._userInvoked) {
            this._invokeGQLuser();
        }
        return this._currentUserSubject;
    }

    get client() {
        return this._client;
    }
}


const GraphQlClient = (() => {
    const client = new GraphQlClientService();
    return client;
})();
export default GraphQlClient;
